/**
 * The far end of an onboarding START link (PNK-253) — `/onboard/<token>`, drawn by `views/OnboardingStart.vue`.
 *
 * 🔴 The link grants nothing. Both calls are unauthenticated (the person following it may not be able to sign in yet),
 * and neither ever carries or answers the person's address in full: the page shows it masked, and pressing the button
 * asks Pinksy to email the person their own onboarding link, to the address on record.
 */
import { API_BASE_URL } from "@/api/base";
import type { FetchLike } from "./http";

export const START_PATH = "/onboarding/start";
export const START_TIMEOUT_MS = 20_000;

export interface StartPreview {
  productionTitle: string;
  position: string | null;
  /** `p••••@s••••.co.za` — never the address. */
  maskedEmail: string;
  /** False: onboarding email is switched off here, and pressing sends nothing. */
  mailEnabled: boolean;
  /** True: the person can already sign in, and the page offers that too. */
  canSignIn: boolean;
}

/** Why a press sent nothing — see `services/user/onboarding-start.ts`. */
export type StartRefusal = "mailOff" | "throttled" | "notDelivered" | "unavailable";

export interface StartResult {
  sent: boolean;
  maskedEmail: string;
  reason: StartRefusal | null;
}

/**
 * `retry`: the link may well be fine — the phone had no signal, the server was busy (429) or failing (5xx). The page
 * then says "try again" rather than "this link is not valid", so a person on a weak signal is not told their link is dead.
 */
export type PreviewOutcome = { ok: true; preview: StartPreview } | { ok: false; message: string | null; retry: boolean };
export type StartOutcome = { ok: true; result: StartResult } | { ok: false; message: string | null; retry: boolean };

/** A status worth pressing again for — busy, or the server's own failure. A 4xx other than 429 is the link itself. */
const retryable = (status: number): boolean => status === 429 || status >= 500;

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() !== "" ? value.trim() : null);
const REFUSALS: ReadonlyArray<StartRefusal> = ["mailOff", "throttled", "notDelivered", "unavailable"];

async function call(token: string, method: "GET" | "POST", base: string, fetchImpl: FetchLike | undefined): Promise<{ response: Response } | { failure: true }> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), START_TIMEOUT_MS);
  try {
    const response = await (fetchImpl ?? fetch)(`${base}${START_PATH}/${encodeURIComponent(token)}`, {
      method,
      signal: abort.signal,
      ...(method === "POST" ? { headers: { "content-type": "application/json" }, body: "{}" } : {}),
    });
    return { response };
  } catch {
    return { failure: true };
  } finally {
    clearTimeout(timer);
  }
}

/** The server's own sentence — one for every bad link, so passing it through leaks nothing. Null: say our own. */
async function messageOf(response: Response): Promise<string | null> {
  try {
    return text(((await response.json()) as { message?: unknown }).message);
  } catch {
    return null;
  }
}

export async function readStart(token: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<PreviewOutcome> {
  const sent = await call(token, "GET", base, fetchImpl);
  if ("failure" in sent) return { ok: false, message: null, retry: true };
  if (!sent.response.ok) return { ok: false, message: await messageOf(sent.response), retry: retryable(sent.response.status) };
  const body = (await sent.response.json()) as Partial<{ productionTitle: unknown; position: unknown; maskedEmail: unknown; mailEnabled: unknown; signInUrl: unknown }>;
  return {
    ok: true,
    preview: {
      productionTitle: text(body.productionTitle) ?? "",
      position: text(body.position),
      maskedEmail: text(body.maskedEmail) ?? "",
      mailEnabled: body.mailEnabled === true,
      canSignIn: text(body.signInUrl) !== null,
    },
  };
}

export async function startOnboarding(token: string, base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<StartOutcome> {
  const sent = await call(token, "POST", base, fetchImpl);
  if ("failure" in sent) return { ok: false, message: null, retry: true };
  if (!sent.response.ok) return { ok: false, message: await messageOf(sent.response), retry: retryable(sent.response.status) };
  const body = (await sent.response.json()) as Partial<{ sent: unknown; maskedEmail: unknown; reason: unknown }>;
  const reason = REFUSALS.find((candidate) => candidate === body.reason) ?? null;
  return { ok: true, result: { sent: body.sent === true, maskedEmail: text(body.maskedEmail) ?? "", reason: body.sent === true ? null : (reason ?? "unavailable") } };
}
